export const CUSTOMER_ETA_CAP_MINUTES = 30;

const hasOwn = (object, key) =>
  Object.prototype.hasOwnProperty.call(object || {}, key);

export const normalizeLegacyQueueRescueLevel = value => {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return 0;
    return Math.max(0, Math.min(2, Math.trunc(value)));
  }

  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, '');

  if (
    ['2', 'level2', 'l2', 'critical', 'severe', 'rescuecritical'].includes(
      normalized
    )
  ) {
    return 2;
  }

  if (
    ['1', 'level1', 'l1', 'rescue', 'rescued', 'rescuestarted'].includes(
      normalized
    )
  ) {
    return 1;
  }

  return 0;
};

export const getLegacyQueueMetadata = conversation =>
  conversation?.additional_attributes?.legacy_queue ||
  conversation?.additionalAttributes?.legacy_queue ||
  conversation?.legacy_queue ||
  conversation?.legacyQueue ||
  {};

const hasFinishedWaitingRound = conversation => {
  const serviceState =
    conversation?.service_state || conversation?.serviceState || '';
  if (serviceState === 'served') return true;

  if (hasOwn(conversation, 'waiting_since')) {
    return !conversation.waiting_since;
  }

  if (hasOwn(conversation, 'waitingSince')) {
    return !conversation.waitingSince;
  }

  return false;
};

const parseWaitingTimestamp = value => {
  if (typeof value === 'number') {
    return value < 1_000_000_000_000 ? value * 1000 : value;
  }

  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const isMetadataForActiveWaitingRound = (conversation, metadata) => {
  if (!metadata.waiting_since) return true;

  const waitingSince =
    conversation.waiting_since ?? conversation.waitingSince ?? null;
  if (!waitingSince) return true;

  const conversationTimestamp = parseWaitingTimestamp(waitingSince);
  const metadataTimestamp = parseWaitingTimestamp(metadata.waiting_since);
  if (conversationTimestamp === null || metadataTimestamp === null) return false;

  return Math.abs(conversationTimestamp - metadataTimestamp) < 1000;
};

export const getLegacyQueueRescueLevel = conversation => {
  if (!conversation || hasFinishedWaitingRound(conversation)) return 0;

  const metadata = getLegacyQueueMetadata(conversation);
  const topLevel = normalizeLegacyQueueRescueLevel(
    conversation.rescue_level ?? conversation.rescueLevel
  );
  if (topLevel > 0) return topLevel;

  if (!isMetadataForActiveWaitingRound(conversation, metadata)) return 0;

  const metadataLevel = normalizeLegacyQueueRescueLevel(
    metadata.rescue_level ?? metadata.rescueLevel ?? metadata.level
  );
  if (metadataLevel > 0) return metadataLevel;

  if (
    metadata.rescue_level_2_at ||
    metadata.level_2_at ||
    metadata.level2_at ||
    metadata.critical_at ||
    metadata.severe_at
  ) {
    return 2;
  }

  if (
    metadata.rescue_level_1_at ||
    metadata.level_1_at ||
    metadata.level1_at ||
    metadata.rescue_started_at ||
    metadata.released_waiting_since
  ) {
    return 1;
  }

  return 0;
};

export const isLegacyQueueRescueConversation = conversation =>
  getLegacyQueueRescueLevel(conversation) > 0;

export const capCustomerEtaMinutes = etaMinutes => {
  const parsedEta = Number(etaMinutes);
  if (!Number.isFinite(parsedEta) || parsedEta <= 0) return 0;

  return Math.min(Math.ceil(parsedEta), CUSTOMER_ETA_CAP_MINUTES);
};

export const partitionRescueConversationIds = (
  conversationIds,
  getConversation
) =>
  conversationIds.reduce(
    (result, id) => {
      if (isLegacyQueueRescueConversation(getConversation(id))) {
        result.rescueIds.push(id);
      } else {
        result.safeIds.push(id);
      }
      return result;
    },
    { safeIds: [], rescueIds: [] }
  );
